/**
 * StreamingSessionReducer — Pure delta-reduction logic for a single streaming session.
 *
 * Owns ALL transient streaming state (thinking accumulators, content buffers, ID counters)
 * and exposes methods that take a timeline + event payload and return a new timeline.
 *
 * Pure (no React), so it's testable in isolation and each concurrent session
 * owns its own instance. `WebSocketPool` wires WS events → reducer → Zustand store.
 */

import { ErrorService } from '@/lib/errors/ErrorService';
import type {
  ServerFinishingCause,
  TimelineItem,
  ThinkingState,
  ToolEvent,
} from '../types/session.types';
import type {
  LegalQuestion,
  Jurisdiction,
  FondementJuridique,
  QuestionProgress,
  JurisprudenceSource,
} from '../components/jurisprudence/types';
import type { ParsedJurisprudence } from '@/utils/jurisprudenceParser';
import type {
  ResolvedAgentUiAction,
  SkillChangeRequest,
  SkillChangeResult,
  RedactionPlanDraftUpdated,
  RedactionPlanDraftUpdateAcknowledged,
  RedactionPlanLaunchStarted,
  TurnDisplayEntry,
} from '@hector/shared-types';

// ============================================================================
// Payload types — match the shapes passed to MessageHandler callbacks
// ============================================================================

/** Payload shape for `agent_thinking` events. */
export interface ThinkingDelta {
  delta: string;
}

/** Payload shape for `agent_message_delta` events. */
export interface ContentDelta {
  delta: string;
}

/** Payload shape for `backend_tool` events. */
export interface ToolPayload {
  tool: ToolEvent['tool'];
  status: ToolEvent['status'];
  data?: ToolEvent['data'];
}

/**
 * Capture les données de progression d'un item `redaction_plan_progress` au
 * moment du flip vers `redaction_plan_results` — elles étaient jetées avant
 * (2026-07-13), rendant le bandeau final non-dépliable. `undefined` quand
 * l'item n'est pas un progress ou n'a encore rien accumulé (ex. declined
 * pré-lancement, jobs generation sans recherche JP).
 */
function captureProgressSnapshot(
  item: TimelineItem,
): Extract<TimelineItem, { type: 'redaction_plan_results' }>['progressSnapshot'] {
  if (item.type !== 'redaction_plan_progress') return undefined;
  const hasData = Boolean(item.argumentTitles?.length || item.searchQueries?.length);
  if (!hasData) return undefined;
  return {
    ...(item.argumentTitles ? { argumentTitles: item.argumentTitles } : {}),
    ...(item.completedQueries !== undefined ? { completedQueries: item.completedQueries } : {}),
    ...(item.totalQueries !== undefined ? { totalQueries: item.totalQueries } : {}),
    ...(item.searchQueries ? { searchQueries: item.searchQueries } : {}),
    ...(item.legalVerification ? { legalVerification: item.legalVerification } : {}),
    ...(item.skipJurisprudence !== undefined ? { skipJurisprudence: item.skipJurisprudence } : {}),
  };
}

/** Payload shape for `jurisprudence_search_request` events. */
export interface JurisprudenceFormPayload {
  jobId: string;
  questions: LegalQuestion[] | string;
  jurisdictions?: Jurisdiction[];
  fondements?: FondementJuridique[];
  autoApprove?: boolean;
}

/** Payload shape for `jurisprudence_search_progress` events. */
export interface JurisprudenceProgressPayload {
  jobId: string;
  questions: Array<{
    id: string;
    progress: number;
    phase?: string;
  }>;
}

/** Payload shape for `jurisprudence_search_results` events. */
export interface JurisprudenceResultsPayload {
  jobId: string;
  response_text: string;
  sources: JurisprudenceSource[];
  jurisprudences: Array<{
    id: string;
    title: string;
    reference: string;
    official_summary: string;
    principles: Array<{ title: string; content: string; citation: string }>;
    context: string;
    dispositif: string;
    related_jurisprudences?: string;
    cited_articles?: string;
    source_questions?: string[];
    visa?: Array<{ title: string; url: string | null }>;
    rapprochements?: Array<{ text: string; url: string | null }>;
  }>;
  fondements_used?: FondementJuridique[];
}

/** Payload shape for `case_wiki_reset_request` events. */
export interface CaseWikiResetRequestPayload {
  caseId: string;
  requestId: string;
  caseTitle?: string | null;
  clientName?: string | null;
  caseWikiContextUserInputs?: string;
}

/** Payload shape for `case_wiki_reset_result` events (verdict backend). */
export interface CaseWikiResetResultPayload {
  caseId: string;
  requestId: string;
  caseWikiContextUserInputs?: string;
  launched: boolean;
  jobId?: string;
  alreadyRunning?: boolean;
  declined?: boolean;
  timedOut?: boolean;
  error?: boolean;
  userMessage?: string;
}

export interface DeletePiecesConfirmationRequestPayload {
  caseId: string;
  caseTitle?: string;
  requestId: string;
  pieces: Array<{ documentId: string; title: string; pieceNumber?: string }>;
}

export interface DeletePiecesConfirmationResultPayload {
  caseId: string;
  requestId: string;
  deleted: boolean;
  deletedCount?: number;
  s3ErrorCount?: number;
  declined?: boolean;
  timedOut?: boolean;
  error?: boolean;
  userMessage?: string;
}

/**
 * Payload shape for `agent_ui_actions` events. Pas de `requestId` : rien n'attend
 * de réponse côté agent (cf. `applyAgentUiActions`).
 */
export interface AgentUiActionsPayload {
  actions: ResolvedAgentUiAction[];
}

/** Reason passed to `finalize()`. */
export type DoneReason = 'complete' | 'stopped' | 'timeout' | 'error' | 'server_shutdown';

// ============================================================================
// StreamingSessionReducer
// ============================================================================

/**
 * Pure-ish reducer for streaming session timeline state.
 *
 * Each `apply*` method takes the current timeline and event data, returns a new timeline.
 * Internal accumulators (thinking content, streaming content, IDs) are mutated as side effects.
 */
export class StreamingSessionReducer {
  // --- Thinking state ---
  private activeThinkingId: string | null = null;
  private thinkingCounter = 0;
  private thinkingContent = '';
  private thinkingStartTime = 0;

  // --- Content state ---
  private streamingContent = '';
  private currentContentId: string | null = null;
  private currentSegmentContent = '';

  // --- Control state ---
  private isCancelled = false;
  private idCounter = 0;
  /**
   * Départage les ids bâtis sur l'heure. Le rattrapage d'un tour (#1888) applique des
   * dizaines de trames dans la même milliseconde : deux outils sans `invocation_id` ou
   * deux notes y recevaient le même id (clés React en double, corrélation sur la mauvaise
   * carte). Jamais remis à zéro : il n'a qu'à ne pas se répéter.
   */
  private timestampIdSeq = 0;

  // --- Jurisprudence state ---
  private jurisprudenceQuestions: Map<string, LegalQuestion[]> = new Map();

  // --- ID generation (same patterns as the hook) ---

  /** Generate a sequential timeline item ID. */
  private nextId(): string {
    return `tl-${++this.idCounter}`;
  }

  /** Suffixe d'id horodaté, unique même au sein d'une milliseconde (voir `timestampIdSeq`). */
  private nextTimestampId(): string {
    return `${Date.now()}-${++this.timestampIdSeq}`;
  }

  /** Oublie le texte et le raisonnement en cours d'accumulation, sans toucher aux compteurs d'ids. */
  private resetStreamAccumulators(): void {
    this.activeThinkingId = null;
    this.thinkingContent = '';
    this.thinkingStartTime = 0;
    this.streamingContent = '';
    this.currentContentId = null;
    this.currentSegmentContent = '';
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  /**
   * Finalize the active thinking block: mark it as inactive, compute duration,
   * and reset accumulators for the next thinking block.
   */
  private finalizeThinking(timeline: TimelineItem[]): TimelineItem[] {
    if (!this.thinkingContent || !this.thinkingStartTime || !this.activeThinkingId) {
      return timeline;
    }

    const finalId = this.activeThinkingId;
    const finalThinking: ThinkingState = {
      content: this.thinkingContent,
      isActive: false,
      startedAt: this.thinkingStartTime,
      durationMs: Date.now() - this.thinkingStartTime,
    };

    // Reset for the next thinking block
    this.thinkingContent = '';
    this.thinkingStartTime = 0;
    this.activeThinkingId = null;

    return timeline.map(item =>
      item.id === finalId ? { ...item, thinking: finalThinking } : item,
    );
  }

  /**
   * Close the active assistant text segment before appending a non-text timeline
   * item. Without this, later `agent_message_delta` events keep mutating the
   * previous content item above the card, so the card appears to slide downward.
   */
  private closeActiveContentSegment(timeline: TimelineItem[]): TimelineItem[] {
    const closingContentId = this.currentContentId;
    let result = timeline;

    if (closingContentId) {
      result = result.map(item => {
        if (item.id !== closingContentId || item.type !== 'content') return item;
        // trimEnd(): some models emit trailing blank lines just before tool/cards.
        // Keeping them creates phantom markdown gaps before the non-text item.
        const trimmed = item.content.trimEnd();
        return trimmed === item.content ? item : { ...item, content: trimmed };
      });
    }

    this.currentContentId = null;
    this.currentSegmentContent = '';
    return result;
  }

  // ============================================================================
  // Public API — delta application
  // ============================================================================

  /**
   * Apply an `agent_thinking` delta. Creates a new thinking item on the first delta,
   * updates the existing one on subsequent deltas.
   */
  applyThinkingDelta(timeline: TimelineItem[], delta: ThinkingDelta): TimelineItem[] {
    if (this.isCancelled) return timeline;

    this.thinkingContent += delta.delta;
    if (!this.thinkingStartTime) this.thinkingStartTime = Date.now();

    // Create a new thinking item if none is active
    if (!this.activeThinkingId) {
      this.activeThinkingId = `thinking-${this.thinkingCounter++}`;
    }

    const currentId = this.activeThinkingId;
    const thinkingState: ThinkingState = {
      content: this.thinkingContent,
      isActive: true,
      startedAt: this.thinkingStartTime,
    };

    const exists = timeline.some(item => item.id === currentId);
    if (exists) {
      return timeline.map(item =>
        item.id === currentId ? { ...item, thinking: thinkingState } : item,
      );
    }
    return [...timeline, { type: 'thinking' as const, id: currentId, thinking: thinkingState }];
  }

  /**
   * Apply an `agent_message_delta`. Finalizes any active thinking block,
   * accumulates content, and creates or updates the current content segment.
   */
  applyContentDelta(timeline: TimelineItem[], delta: ContentDelta): TimelineItem[] {
    if (this.isCancelled) return timeline;

    // Finalize thinking before content starts
    const result = this.finalizeThinking(timeline);

    this.streamingContent += delta.delta;
    this.currentSegmentContent += delta.delta;

    const segmentContent = this.currentSegmentContent;
    const contentId = this.currentContentId;

    if (contentId) {
      // Update existing content segment
      return result.map(item =>
        item.id === contentId ? { ...item, content: segmentContent } : item,
      );
    }

    // Create new content segment
    const newId = this.nextId();
    this.currentContentId = newId;
    return [...result, { type: 'content' as const, id: newId, content: segmentContent }];
  }

  /**
   * Apply an `agent_message` snapshot (full content replacement).
   * Used as a fallback when deltas are missed.
   *
   * When tool calls have split the content into multiple segments, the snapshot
   * contains ALL content (across all segments). We extract only the portion
   * belonging to the current segment to avoid duplicating text already displayed
   * in previous segment items.
   */
  applyContentSnapshot(timeline: TimelineItem[], content: string): TimelineItem[] {
    if (this.isCancelled) return timeline;

    // Compute where the current segment starts within the full content stream
    const segmentStart = this.streamingContent.length - this.currentSegmentContent.length;
    this.streamingContent = content;
    const segmentOnly = content.slice(segmentStart);
    this.currentSegmentContent = segmentOnly;

    const contentId = this.currentContentId;
    if (contentId) {
      return timeline.map(item =>
        item.id === contentId ? { ...item, content: segmentOnly } : item,
      );
    }

    const newId = this.nextId();
    this.currentContentId = newId;
    return [...timeline, { type: 'content' as const, id: newId, content: segmentOnly }];
  }

  /**
   * Apply a `backend_tool` event.
   *
   * Canvas may announce an ephemeral `preparing` state while the LLM still
   * serializes a long payload. It is materialized only with a stable
   * `invocation_id`, then promoted in place by `started`; therefore the lawyer
   * never sees a duplicate card for one eventual Canvas action.
   */
  applyToolEvent(timeline: TimelineItem[], payload: ToolPayload): TimelineItem[] {
    if (this.isCancelled) return timeline;

    // Finalize thinking before tool events
    let result = this.finalizeThinking(timeline);

    // Identité de la carte : on préfère l'`invocation_id` backend (= tool_use.id,
    // unique par appel) — indispensable pour corréler plusieurs invoke_subagent
    // concurrents. Fallback sur l'id synthétique pour les tools sans invocation_id.
    const invocationId = payload.data?.invocation_id;
    const toolId = invocationId ?? `${payload.tool}-${payload.data?.query || ''}-${this.nextTimestampId()}`;

    // A preparation without the correlation key could never be promoted and
    // would survive as a false action. The backend is required to provide it.
    if (payload.status === 'preparing' && !invocationId) return result;

    // Corréler le status update à la BONNE carte.
    // - Avec invocation_id : match exact par identité d'appel → robuste même si
    //   plusieurs invoke_subagent du même tour sont in-flight simultanément.
    // - Sans (tools legacy / non parallélisables) : comportement historique =
    //   premier tool non terminé portant le même nom.
    //   ⚠️ Sans identité, un `started` est TOUJOURS un nouvel appel : la
    //   déduplication des `started` ci-dessous n'a de sens qu'avec un
    //   invocation_id (promotion preparing → started). L'appliquer ici masquait
    //   le second appel d'un outil sans identité dès que le terminal du premier
    //   manquait (revue adversariale #1571, 2026-09-17).
    const existingToolIdx = invocationId !== undefined
      ? result.findIndex(item =>
          item.type === 'tool' && item.event.invocation_id === invocationId,
        )
      : payload.status === 'started'
        ? -1
        : result.findIndex(item =>
            item.type === 'tool' && item.event.tool === payload.tool &&
            item.event.status !== 'completed' && item.event.status !== 'error',
          );

    if (existingToolIdx >= 0) {
      const existing = result[existingToolIdx] as { type: 'tool'; id: string; event: ToolEvent };
      const recoversTransportInterruption =
        existing.event.transportInterrupted === true && payload.status !== 'preparing';

      // Duplicate preparation frames are expected while the model is still
      // producing its arguments; keep the original card and timestamp.
      if (payload.status === 'preparing') return result;

      // `started` only promotes an unexecuted preparation. A late/replayed
      // started frame must never reopen an already running or terminal card —
      // except the local synthetic error created by a transport interruption.
      if (
        payload.status === 'started' &&
        existing.event.status !== 'preparing' &&
        !recoversTransportInterruption
      ) return result;

      // A terminal event is authoritative, but never let a duplicate terminal
      // (or an out-of-order in-progress frame) regress a settled action. The
      // disconnect marker is not a backend terminal: the replayed lifecycle is
      // authoritative and clears it below.
      if (
        (payload.status === 'in_progress' || payload.status === 'completed' || payload.status === 'error') &&
        (existing.event.status === 'completed' || existing.event.status === 'error') &&
        !recoversTransportInterruption
      ) {
        return result;
      }

      return result.map((item, i) => {
        if (i !== existingToolIdx) return item;
        const current = item as { type: 'tool'; id: string; event: ToolEvent };
        const nextEvent: ToolEvent = {
          ...current.event,
          status: payload.status,
          // Lifecycle telemetry may omit the nominal Canvas action after the
          // early frame; preserve the safe presentation data already known.
          data: payload.data === undefined
            ? current.event.data
            : { ...current.event.data, ...payload.data },
        };
        if (recoversTransportInterruption) {
          delete nextEvent.transportInterrupted;
        }
        return {
          ...current,
          event: nextEvent,
        };
      });
    }

    if (payload.status === 'preparing' || payload.status === 'started') {
      // A brand-new activity, whether announced early or executed immediately,
      // closes the preceding assistant text segment exactly once.
      result = this.closeActiveContentSegment(result);
      const newEvent: ToolEvent = {
        id: toolId,
        tool: payload.tool,
        status: payload.status,
        data: payload.data,
        timestamp: new Date().toISOString(),
        // Identité stable pour la corrélation des updates suivants (cf. ci-dessus).
        invocation_id: invocationId,
      };
      return [...result, { type: 'tool' as const, id: toolId, event: newEvent }];
    }

    return result;
  }

  /**
   * Apply a `jurisprudence_search_request` event.
   * Parses questions (handling the backend's string-with-trailing-garbage format)
   * and adds a form item to the timeline.
   */
  applyJurisprudenceForm(timeline: TimelineItem[], payload: JurisprudenceFormPayload): TimelineItem[] {
    if (this.isCancelled) return timeline;

    const result = this.closeActiveContentSegment(this.finalizeThinking(timeline));

    const jobId = payload.jobId;

    // Backend may send `questions` as a JSON string with trailing garbage
    let questions: LegalQuestion[] = [];
    if (typeof payload.questions === 'string') {
      try {
        const raw = payload.questions;
        const start = raw.indexOf('[');
        const end = raw.lastIndexOf(']');
        const jsonStr = start >= 0 && end > start ? raw.slice(start, end + 1) : raw;
        const parsed: unknown = JSON.parse(jsonStr);
        questions = Array.isArray(parsed) ? parsed as LegalQuestion[] : [];
      } catch {
        ErrorService.handle(
          new Error('Failed to parse agent questions JSON'),
          { context: { action: 'parse_agent_questions' }, silent: true },
        );
      }
    } else if (Array.isArray(payload.questions)) {
      questions = payload.questions;
    }

    this.jurisprudenceQuestions.set(jobId, questions);

    const itemId = this.nextId();
    return [...result, {
      type: 'jurisprudence_form' as const,
      id: itemId,
      jobId,
      questions,
      jurisdictions: payload.jurisdictions ?? ['CC', 'CA', 'TJ', 'TCOM'] as Jurisdiction[],
      fondements: payload.fondements ?? [],
      autoApprove: payload.autoApprove ?? false,
    }];
  }

  /**
   * Apply a `jurisprudence_search_progress` event.
   * Merges incoming question progress into the existing progress item,
   * or seeds a new one from the stored questions.
   */
  applyJurisprudenceProgress(timeline: TimelineItem[], payload: JurisprudenceProgressPayload): TimelineItem[] {
    if (this.isCancelled) return timeline;

    let result = timeline;

    const jobId = payload.jobId;

    // Une recherche dont ce réducteur n'a vu ni le formulaire ni la carte : celle d'un tour
    // repris après rechargement (#1888), dont les cartes interactives ne sont pas rattrapées.
    // Sans le texte des questions, la carte afficherait leurs identifiants bruts. Le résultat
    // de la recherche, lui, se suffit et s'affiche à son arrivée.
    if (
      !this.jurisprudenceQuestions.has(jobId)
      && !timeline.some(item => item.type === 'jurisprudence_progress' && item.jobId === jobId)
    ) {
      return timeline;
    }

    const storedQuestions = this.jurisprudenceQuestions.get(jobId) ?? [];

    // Each event contains only ONE question — merge into existing state
    const incoming = new Map(
      payload.questions.map(q => [q.id, {
        id: q.id,
        questionText: storedQuestions.find(oq => oq.id === q.id)?.text ?? q.id,
        progress: q.progress,
        phase: q.phase,
      } as QuestionProgress]),
    );

    const progressIdx = result.findIndex(
      item => item.type === 'jurisprudence_progress' && item.jobId === jobId,
    );

    if (progressIdx >= 0) {
      const existing = (result[progressIdx] as { questions: QuestionProgress[] }).questions;
      // Merge: update existing questions, append new ones
      const merged = existing.map(eq => incoming.get(eq.id) ?? eq);
      for (const [id, q] of incoming) {
        if (!existing.some(eq => eq.id === id)) merged.push(q);
      }
      // CAST: le spread { ...item, questions: merged } perd le narrowing de l'union TimelineItem ;
      // on sait par construction qu'on remplace un 'jurisprudence_progress' par le même type.
      return result.map((item, i) =>
        i === progressIdx ? { ...item, questions: merged } : item,
      ) as TimelineItem[];
    }

    // First progress event — seed with all known questions (0% for unseen)
    const allQuestions: QuestionProgress[] = storedQuestions.map(sq =>
      incoming.get(sq.id) ?? {
        id: sq.id,
        questionText: sq.text,
        progress: 0,
        phase: undefined,
      },
    );

    // Replace the form item if it exists
    const formIdx = result.findIndex(
      item => item.type === 'jurisprudence_form' && item.jobId === jobId,
    );
    if (formIdx >= 0) {
      return result.map((item, i) =>
        i === formIdx
          ? { type: 'jurisprudence_progress' as const, id: item.id, jobId, questions: allQuestions }
          : item,
      );
    }

    result = this.closeActiveContentSegment(this.finalizeThinking(result));

    return [...result, {
      type: 'jurisprudence_progress' as const,
      id: this.nextId(),
      jobId,
      questions: allQuestions,
    }];
  }

  /**
   * Apply a `jurisprudence_search_results` event.
   * Replaces the matching progress/form item with a results item.
   */
  applyJurisprudenceResults(timeline: TimelineItem[], payload: JurisprudenceResultsPayload): TimelineItem[] {
    if (this.isCancelled) return timeline;

    const jobId = payload.jobId;
    const result = this.closeActiveContentSegment(this.finalizeThinking(timeline));

    // Map backend wire format to ParsedJurisprudence
    const jurisprudences: ParsedJurisprudence[] = payload.jurisprudences.map(jp => ({
      id: jp.id,
      title: jp.title,
      reference: jp.reference,
      officialSummary: jp.official_summary,
      principles: jp.principles,
      context: jp.context,
      dispositif: jp.dispositif,
      relevance: undefined as string | undefined,
      relatedJurisprudences: jp.related_jurisprudences ?? '',
      citedArticles: jp.cited_articles ?? '',
      sourceQuestions: jp.source_questions,
      visa: jp.visa,
      rapprochements: jp.rapprochements,
    }));

    // Find the progress or form item matching this jobId (search from end)
    let targetIdx = -1;
    for (let i = result.length - 1; i >= 0; i--) {
      const item = result[i];
      if (item && (item.type === 'jurisprudence_progress' || item.type === 'jurisprudence_form') && item.jobId === jobId) {
        targetIdx = i;
        break;
      }
    }

    const resultsItem: TimelineItem = {
      type: 'jurisprudence_results' as const,
      id: targetIdx >= 0 ? (result[targetIdx]?.id ?? this.nextId()) : this.nextId(),
      jobId,
      responseText: payload.response_text,
      sources: payload.sources,
      jurisprudences,
      fondements: (payload.fondements_used ?? []) as FondementJuridique[],
    };

    this.jurisprudenceQuestions.delete(jobId);

    if (targetIdx >= 0) {
      return result.map((item, i) => i === targetIdx ? resultsItem : item);
    }
    return [...result, resultsItem];
  }

  /**
   * Apply a `case_wiki_reset_request` event — append the destructive reset card.
   *
   * Le `requestId` est stocké dans l'item pour être renvoyé verbatim au clic
   * (nonce de corrélation : une réponse au mauvais requestId est ignorée backend).
   * Le repli « en cours » (clic « Lancer ») est un état LOCAL de la carte, pas
   * une transition de timeline — contrairement à JP, il n'y a pas de barres de
   * progression à amorcer (la progression se suit dans l'onglet Analyse avancée).
   */
  applyCaseWikiResetRequest(
    timeline: TimelineItem[],
    payload: CaseWikiResetRequestPayload,
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;

    const result = this.closeActiveContentSegment(this.finalizeThinking(timeline));
    const existingIdx = result.findIndex(
      item => item.type === 'case_wiki_reset' && item.requestId === payload.requestId,
    );
    if (existingIdx >= 0) {
      // WebSocket resume/replay can deliver the original request after the ACK.
      // Keep the authoritative verdict on the existing card instead of showing a
      // fresh pending card that looks like the analysis restarted.
      return result.map((item, i) =>
        i === existingIdx && item.type === 'case_wiki_reset'
          ? {
              ...item,
              caseId: payload.caseId,
              caseTitle: payload.caseTitle,
              clientName: payload.clientName,
              caseWikiContextUserInputs: payload.caseWikiContextUserInputs,
            }
          : item,
      );
    }

    return [...result, {
      type: 'case_wiki_reset' as const,
      id: this.nextId(),
      caseId: payload.caseId,
      requestId: payload.requestId,
      caseTitle: payload.caseTitle,
      clientName: payload.clientName,
      caseWikiContextUserInputs: payload.caseWikiContextUserInputs,
    }];
  }

  /**
   * Apply a `case_wiki_reset_result` event — verdict backend faisant autorité.
   *
   * Pose `result` sur l'item `case_wiki_reset` dont le `requestId` matche.
   * Aucun match signifie verdict obsolète : il est ignoré afin qu'une ancienne
   * demande ne puisse jamais fermer ou valider la carte active.
   */
  applyCaseWikiResetResult(
    timeline: TimelineItem[],
    payload: CaseWikiResetResultPayload,
  ): TimelineItem[] {
    const idx = timeline.findIndex(
      item => item.type === 'case_wiki_reset' && item.requestId === payload.requestId,
    );
    if (idx < 0) return timeline; // stale / no matching card

    return timeline.map((item, i) =>
      i === idx && item.type === 'case_wiki_reset'
        ? {
            ...item,
            result: {
              launched: payload.launched,
              jobId: payload.jobId,
              alreadyRunning: payload.alreadyRunning,
              declined: payload.declined,
              timedOut: payload.timedOut,
              error: payload.error,
              userMessage: payload.userMessage,
            },
          }
        : item,
    );
  }

  /** Ajoute/rafraîchit la carte de consentement sans dupliquer un replay WS. */
  applyDeletePiecesConfirmationRequest(
    timeline: TimelineItem[],
    payload: DeletePiecesConfirmationRequestPayload,
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    const result = this.closeActiveContentSegment(this.finalizeThinking(timeline));
    const existingIdx = result.findIndex(
      item => item.type === 'delete_pieces_confirmation' && item.requestId === payload.requestId,
    );
    if (existingIdx >= 0) {
      return result.map((item, index) =>
        index === existingIdx && item.type === 'delete_pieces_confirmation'
          ? { ...item, caseId: payload.caseId, caseTitle: payload.caseTitle, pieces: payload.pieces }
          : item,
      );
    }
    return [...result, {
      type: 'delete_pieces_confirmation' as const,
      id: this.nextId(),
      caseId: payload.caseId,
      caseTitle: payload.caseTitle,
      requestId: payload.requestId,
      pieces: payload.pieces,
    }];
  }

  /** Pose le verdict seulement sur la carte dont le nonce correspond. */
  applyDeletePiecesConfirmationResult(
    timeline: TimelineItem[],
    payload: DeletePiecesConfirmationResultPayload,
  ): TimelineItem[] {
    return timeline.map(item =>
      item.type === 'delete_pieces_confirmation' && item.requestId === payload.requestId
        ? {
            ...item,
            result: {
              deleted: payload.deleted,
              deletedCount: payload.deletedCount,
              s3ErrorCount: payload.s3ErrorCount,
              declined: payload.declined,
              timedOut: payload.timedOut,
              error: payload.error,
              userMessage: payload.userMessage,
            },
          }
        : item,
    );
  }

  // ============================================================================
  // Redaction plan request/launch/progress/results
  // ============================================================================

  /** Pousse la carte de validation temporisée du plan initial dans la timeline. */
  applyRedactionPlanRequest(
    timeline: TimelineItem[],
    payload: {
      requestId: string;
      kind: 'research';
      jobId: string;
      autoLaunchAt?: string;
      proposedPayload: {
        nom_acte?: string | null;
        specialisation?: string | null;
        juridictions?: string[];
        additional_instructions?: string;
        original_job_id?: string;
        generation_mode?: 'full' | 'section_i' | 'section_ii_iii';
        /** Proposition du LLM sur la recherche JP, figée par l'accusé backend. */
        skip_jurisprudence?: boolean;
        /**
         * Lancement en un seul temps : le job produit l'acte complet, sans plan
         * intermédiaire à valider. Transite déjà à l'exécution (l'objet vient de la trame
         * WS, où le champ est déclaré) ; le déclarer ici aligne le contrat sur l'usage.
         */
        full_document?: boolean;
      };
    },
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    return [
      ...timeline,
      {
        type: 'redaction_plan_form' as const,
        id: this.nextId(),
        requestId: payload.requestId,
        jobId: payload.jobId,
        kind: payload.kind,
        ...(payload.autoLaunchAt ? { autoLaunchAt: payload.autoLaunchAt } : {}),
        autoLaunchRevision: 0,
        proposedPayload: payload.proposedPayload,
      },
    ];
  }

  /** Records the latest outbound draft so reconnect can replay exactly that revision. */
  applyRedactionPlanDraftUpdated(
    timeline: TimelineItem[],
    payload: RedactionPlanDraftUpdated['payload'],
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    return timeline.map((item) => {
      if (item.type !== 'redaction_plan_form' || item.requestId !== payload.requestId) return item;
      const currentRevision = item.autoLaunchRevision ?? 0;
      const pendingRevision = item.autoLaunchDraftUpdatePending?.revision ?? 0;
      if (payload.revision <= Math.max(currentRevision, pendingRevision)) return item;
      return { ...item, autoLaunchDraftUpdatePending: payload };
    });
  }

  /** Applies the authoritative deadline while ignoring stale acknowledgements. */
  applyRedactionPlanDraftUpdateAcknowledged(
    timeline: TimelineItem[],
    payload: RedactionPlanDraftUpdateAcknowledged['payload'],
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    return timeline.map((item) => {
      if (item.type !== 'redaction_plan_form' || item.requestId !== payload.requestId) return item;
      const currentRevision = item.autoLaunchRevision ?? 0;
      const pendingRevision = item.autoLaunchDraftUpdatePending?.revision;
      const isPendingAck = pendingRevision === payload.revision;
      if (
        (pendingRevision !== undefined && payload.revision < pendingRevision)
        || (!isPendingAck && payload.revision <= currentRevision)
      ) {
        return item;
      }
      return {
        ...item,
        autoLaunchAt: payload.autoLaunchAt,
        autoLaunchRevision: payload.revision,
        // The pending draft is the only copy of the lawyer's edits. Move it to
        // durable timeline state before clearing the transport-level pending flag,
        // otherwise a remount shows the original proposal while the backend launches
        // the acknowledged snapshot.
        ...(isPendingAck ? {
          autoLaunchDraftSnapshot: item.autoLaunchDraftUpdatePending?.snapshot,
          autoLaunchDraftUpdatePending: undefined,
        } : {}),
      };
    });
  }

  /**
   * Applies the previous backend protocol's ACK during a rolling deployment.
   * The server has disarmed its deadline, but the local snapshot must remain
   * durable so a remount and the later manual click still submit what is shown.
   */
  applyLegacyRedactionPlanInteractionAcknowledged(
    timeline: TimelineItem[],
    requestId: string,
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    return timeline.map((item) => {
      if (item.type !== 'redaction_plan_form' || item.requestId !== requestId) return item;
      const pending = item.autoLaunchDraftUpdatePending;
      return {
        ...item,
        autoLaunchAt: undefined,
        ...(pending ? {
          autoLaunchRevision: Math.max(item.autoLaunchRevision ?? 0, pending.revision),
          autoLaunchDraftSnapshot: pending.snapshot,
          autoLaunchDraftUpdatePending: undefined,
        } : {}),
      };
    });
  }

  /** Applies the backend's single launch acknowledgement for every launch origin. */
  applyRedactionPlanLaunchStarted(
    timeline: TimelineItem[],
    payload: RedactionPlanLaunchStarted['payload'],
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    if (timeline.some((item) =>
      (item.type === 'redaction_plan_progress' || item.type === 'redaction_plan_results')
      && item.requestId === payload.requestId
    )) return timeline;
    let foundForm = false;
    const updated = timeline.map((item) => {
      if (item.type !== 'redaction_plan_form' || item.requestId !== payload.requestId) return item;
      foundForm = true;
      const effectiveSkipJurisprudence = payload.effectivePayload.skip_jurisprudence;
      return {
        type: 'redaction_plan_progress' as const,
        id: item.id,
        requestId: payload.requestId,
        jobId: payload.jobId,
        kind: payload.kind,
        phase: 'planning' as const,
        ...(typeof effectiveSkipJurisprudence === 'boolean'
          ? { skipJurisprudence: effectiveSkipJurisprudence }
          : {}),
        proposedPayload: payload.effectivePayload,
      };
    });
    if (foundForm) return updated;
    return [
      ...timeline,
      {
        type: 'redaction_plan_progress' as const,
        id: this.nextId(),
        requestId: payload.requestId,
        jobId: payload.jobId,
        kind: payload.kind,
        phase: 'planning' as const,
        ...(typeof payload.effectivePayload.skip_jurisprudence === 'boolean'
          ? { skipJurisprudence: payload.effectivePayload.skip_jurisprudence }
          : {}),
        proposedPayload: payload.effectivePayload,
      },
    ];
  }

  /**
   * Forward d'un event worker (Redis Pub/Sub). On dérive la phase de l'event :
   *  - planning_complete / legal_verification_* → reste `planning`
   *  - jp_search_start / research_start / search_query_* → `research`
   *  - aggregation_start → `aggregation`
   *  - aggregation_complete (ou legal_verification_complete sans recherche de
   *    JP) → `preparation` : le worker compose le plan sans rien publier.
   *    Aucun event worker ne pose `insertion` pour un plan : c'est
   *    `applyRedactionWritingStarted`, quand le flux est écouté (ou, en
   *    rattrapage, un verdict `completed` arrivé avant la fin du flux —
   *    `applyRedactionPlanResults`).
   *  - error / job_failed → `error`
   *
   * NB : le canvas latéral écoute son propre SSE pour le contenu — ces events
   * pilotent UNIQUEMENT la carte progress dans la timeline chat.
   */
  applyRedactionPlanProgress(
    timeline: TimelineItem[],
    payload: {
      requestId: string;
      jobId: string;
      kind: 'research' | 'generation';
      event: Record<string, unknown>;
    },
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    const idx = timeline.findIndex(
      (item) => item.type === 'redaction_plan_progress' && item.requestId === payload.requestId,
    );
    if (idx < 0) return timeline; // l'item form n'a pas encore été muté en progress
    const event = payload.event ?? {};
    const eventType = typeof event.type === 'string' ? event.type : '';

    return timeline.map((item, i) => {
      if (i !== idx || item.type !== 'redaction_plan_progress') return item;
      let phase = item.phase;
      let argumentTitles = item.argumentTitles;
      let completedQueries = item.completedQueries;
      let totalQueries = item.totalQueries;
      let errorMessage = item.errorMessage;
      let errorPhase = item.errorPhase;
      let legalVerification = item.legalVerification;
      let searchQueries = item.searchQueries;

      const upsertQuery = (
        list: typeof searchQueries,
        argumentId: string,
        patch: Partial<NonNullable<typeof searchQueries>[number]>,
      ): typeof searchQueries => {
        const base = list ?? [];
        const idx2 = base.findIndex((q) => q.argumentId === argumentId);
        if (idx2 < 0) {
          return [
            ...base,
            {
              index: base.length,
              status: 'processing',
              argumentId,
              argumentTitle: argumentId,
              angle: '',
              ...patch,
            } as NonNullable<typeof searchQueries>[number],
          ];
        }
        return base.map((q, j) => (j === idx2 ? { ...q, ...patch } : q));
      };

      switch (eventType) {
        case 'planning_complete': {
          const titles = Array.isArray(event.argumentTitles) ? (event.argumentTitles as string[]) : undefined;
          argumentTitles = titles ?? argumentTitles;
          totalQueries = typeof event.totalSearchQueries === 'number' ? event.totalSearchQueries : totalQueries;
          break;
        }
        // La vérification des fondements n'est PAS la recherche JP : elle porte sur les
        // articles cités par les moyens, et elle a lieu même quand l'avocat a coupé la
        // recherche. Or sans recherche JP la phase `research` n'existe ni dans la frise
        // (2 pastilles) ni dans les plafonds de progression : la barre se figeait dès
        // `legal_verification_start`, et plus AUCUNE pastille n'était active, jusqu'au
        // verdict final — plusieurs minutes d'écran mort (2026-08-26).
        // Avec recherche JP, on garde le comportement d'origine : `jp_search_start` /
        // `research_start` prennent le relais juste après de toute façon.
        case 'argument_refinement_complete': {
          // Titres RÉORDONNÉS par le raffineur : sans ce cas, la liste affichée restait
          // celle de `planning_complete`, dans un ordre que le worker avait déjà révisé.
          const refined = Array.isArray(event.argumentTitles) ? (event.argumentTitles as string[]) : undefined;
          argumentTitles = refined ?? argumentTitles;
          totalQueries = typeof event.totalSearchQueries === 'number' ? event.totalSearchQueries : totalQueries;
          break;
        }
        case 'legal_verification_start':
          if (!item.skipJurisprudence) phase = 'research';
          legalVerification = {
            status: 'in_progress',
            argumentsCount: typeof event.argumentsCount === 'number' ? event.argumentsCount : undefined,
          };
          break;
        case 'legal_verification_complete':
          // Sans recherche JP, c'est le DERNIER événement avant la fin : le worker compose
          // ensuite le plan, sans rien publier. Or `aggregation_complete`, seul déclencheur
          // habituel de `preparation`, n'est émis que par les services de jurisprudence. On
          // bascule donc ici — sinon l'avocat regarde une barre morte pendant précisément
          // les minutes qui comptent.
          phase = item.skipJurisprudence ? 'preparation' : 'research';
          legalVerification = {
            status: 'complete',
            totalArticlesVerified: typeof event.totalArticlesVerified === 'number' ? event.totalArticlesVerified : undefined,
            verificationTimeMs: typeof event.verificationTimeMs === 'number' ? event.verificationTimeMs : undefined,
          };
          break;
        case 'jp_search_start':
        case 'research_start':
          phase = 'research';
          break;
        case 'search_query_start': {
          phase = 'research';
          const argumentId = typeof event.argumentId === 'string' ? event.argumentId : undefined;
          const argumentTitle = typeof event.argumentTitle === 'string' ? event.argumentTitle : undefined;
          const angle = typeof event.angle === 'string' ? event.angle : '';
          if (argumentId) {
            searchQueries = upsertQuery(searchQueries, argumentId, {
              status: 'processing',
              ...(argumentTitle ? { argumentTitle } : {}),
              angle,
            });
          }
          break;
        }
        case 'fetch_complete': {
          phase = 'research';
          const argumentId = typeof event.argumentId === 'string' ? event.argumentId : undefined;
          const decisionsFound = typeof event.decisionsFound === 'number' ? event.decisionsFound : undefined;
          if (argumentId && decisionsFound !== undefined) {
            searchQueries = upsertQuery(searchQueries, argumentId, { decisionsFound });
          }
          break;
        }
        case 'relevance_batch_progress': {
          phase = 'research';
          const argumentId = typeof event.argumentId === 'string' ? event.argumentId : undefined;
          const current = typeof event.currentBatch === 'number' ? event.currentBatch : undefined;
          const total = typeof event.totalBatches === 'number' ? event.totalBatches : undefined;
          const percent = typeof event.percentComplete === 'number' ? event.percentComplete : undefined;
          if (argumentId && current !== undefined && total !== undefined && percent !== undefined) {
            searchQueries = upsertQuery(searchQueries, argumentId, {
              batchProgress: { current, total, percentComplete: percent },
            });
          }
          break;
        }
        case 'search_query_complete': {
          phase = 'research';
          completedQueries = (completedQueries ?? 0) + 1;
          const argumentId = typeof event.argumentId === 'string' ? event.argumentId : undefined;
          const decisionsRelevant = typeof event.decisionsRelevant === 'number' ? event.decisionsRelevant : undefined;
          const decisionsTotal = typeof event.decisionsTotal === 'number' ? event.decisionsTotal : undefined;
          const duration = typeof event.duration === 'number' ? event.duration : undefined;
          if (argumentId) {
            searchQueries = upsertQuery(searchQueries, argumentId, {
              status: 'complete',
              ...(decisionsRelevant !== undefined ? { decisionsRelevant } : {}),
              ...(decisionsTotal !== undefined ? { decisionsFound: decisionsTotal } : {}),
              ...(duration !== undefined ? { duration } : {}),
            });
          }
          break;
        }
        case 'research_complete':
        case 'jp_search_complete':
        case 'aggregation_start':
          phase = 'aggregation';
          break;
        case 'aggregation_complete':
          // Pas `insertion` : après la synthèse, le worker choisit le modèle du cabinet et
          // compose le plan pendant plusieurs minutes avant que le premier mot n'arrive
          // dans le canvas (7 min mesurées le 2026-10-12, carte figée sur « Insertion »).
          // `applyRedactionWritingStarted` pose `insertion` quand l'écriture commence.
          phase = 'preparation';
          break;
        case 'job_failed':
        case 'error':
          // Mémoriser l'étape atteinte AVANT d'écraser `phase` : c'est la seule
          // information qui permet à la carte de situer la panne pour l'avocat.
          if (phase !== 'error' && phase !== 'complete') errorPhase = phase;
          phase = 'error';
          // Aucun libellé de repli ici. Un « Erreur du job » n'apprend rien et
          // occupait la ligne de détail à la place du vrai message backend
          // quand il arrive. Sans message, la carte formule elle-même une
          // phrase située à partir de `kind` + `errorPhase`.
          errorMessage = typeof event.error === 'string' && event.error.trim().length > 0
            ? event.error
            : errorMessage;
          break;
        default:
          break;
      }
      return {
        ...item,
        phase,
        ...(argumentTitles ? { argumentTitles } : {}),
        ...(completedQueries !== undefined ? { completedQueries } : {}),
        ...(totalQueries !== undefined ? { totalQueries } : {}),
        ...(legalVerification ? { legalVerification } : {}),
        ...(searchQueries ? { searchQueries } : {}),
        ...(errorMessage ? { errorMessage } : {}),
        ...(errorPhase ? { errorPhase } : {}),
      };
    });
  }

  /**
   * L'écriture dans le canvas commence pour ce job (`redaction_job_started` lié et écouté).
   *
   * Le signal qui fait dire « Insertion » à la carte d'un plan ; seul un verdict `completed`
   * arrivé avant la fin du flux la pose aussi (`applyRedactionPlanResults`). Une carte déjà
   * terminée ou en erreur n'en bouge pas : la trame peut être rejouée après une reconnexion.
   */
  applyRedactionWritingStarted(timeline: TimelineItem[], jobId: string): TimelineItem[] {
    if (this.isCancelled) return timeline;
    return timeline.map((item) => {
      if (item.type !== 'redaction_plan_progress' || item.jobId !== jobId) return item;
      if (item.phase === 'complete' || item.phase === 'error') return item;
      return { ...item, phase: 'insertion' as const };
    });
  }

  /**
   * Verdict backend reçu. Deux cas :
   *
   *  A) `verdict === 'completed'` ET on a un progress item actif → BUFFER le
   *     verdict dans l'item progress (`pendingVerdict` + `pendingExtras`). La
   *     carte reste en mode progress jusqu'au end-of-stream SSE côté canvas
   *     (cf. `applyRedactionPlanSseComplete`). Mirror Word add-in : la fin de
   *     carte = fin du job ET fin de l'insertion SSE.
   *
   *  B) Tout autre verdict (declined / cancelled / interrupted /
   *     error) → flip IMMÉDIAT progress→results, car il n'y aura pas de SSE
   *     à attendre (le worker n'a pas tourné, ou pas marqué le job completed,
   *     donc /stream-plan-detaille ne servira jamais rien).
   *
   *  C) Pas de progress item (cas dégénéré) → push un results item direct
   *     (fallback robuste).
   */
  applyRedactionPlanResults(
    timeline: TimelineItem[],
    payload: {
      requestId: string;
      jobId: string;
      kind: 'research' | 'generation';
      verdict: 'completed' | 'declined' | 'interrupted' | 'cancelled' | 'error';
      [k: string]: unknown;
    },
  ): TimelineItem[] {
    const idx = timeline.findIndex((item) =>
      (item.type === 'redaction_plan_progress' || item.type === 'redaction_plan_form') &&
      item.requestId === payload.requestId,
    );
    const extras = { ...payload };
    delete (extras as Record<string, unknown>).requestId;
    delete (extras as Record<string, unknown>).jobId;
    delete (extras as Record<string, unknown>).kind;
    delete (extras as Record<string, unknown>).verdict;

    // Cas C — pas de progress item : push direct
    if (idx < 0) {
      return [
        ...timeline,
        {
          type: 'redaction_plan_results' as const,
          id: this.nextId(),
          requestId: payload.requestId,
          jobId: payload.jobId,
          kind: payload.kind,
          verdict: payload.verdict,
          proposedPayload: {},
          extras,
        },
      ];
    }

    // Cas A — completed + on a un progress item → BUFFER, on flippera plus tard
    // sur SSE onComplete. Indispensable pour que la carte chat continue de
    // tourner pendant que le canvas latéral écrit son plan ou son acte.
    //
    // EXCEPTION (race condition) : si le SSE a déjà fini AVANT le verdict
    // (phase==='complete' déjà posée par applyRedactionPlanSseComplete sans
    // pendingVerdict), on flippe IMMÉDIATEMENT — sinon la carte resterait
    // coincée en progress alors que le canvas est déjà plein. Ordre réel
    // typique = verdict puis SSE end, mais le mocked / fast path peut inverser.
    const match = timeline[idx];
    if (
      payload.verdict === 'completed' &&
      match?.type === 'redaction_plan_progress' &&
      match.phase !== 'complete'
    ) {
      return timeline.map((item, i) => {
        if (i !== idx || item.type !== 'redaction_plan_progress') return item;
        return {
          ...item,
          // On force la phase 'insertion' (= « le canvas est en train d'écrire »)
          // pour que le label UI reflète la vraie étape en cours.
          phase: 'insertion' as const,
          pendingVerdict: payload.verdict,
          pendingExtras: extras,
        };
      });
    }

    // Cas B — autre verdict → flip immédiat (pas de SSE à attendre)
    return timeline.map((item, i) => {
      if (i !== idx) return item;
      const proposedPayload = 'proposedPayload' in item ? item.proposedPayload : {};
      const progressSnapshot = captureProgressSnapshot(item);
      return {
        type: 'redaction_plan_results' as const,
        id: item.id,
        requestId: payload.requestId,
        jobId: payload.jobId,
        kind: payload.kind,
        verdict: payload.verdict,
        proposedPayload,
        extras,
        ...(progressSnapshot ? { progressSnapshot } : {}),
      };
    });
  }

  /**
   * SSE canvas a terminé (end-of-stream sur `/stream-plan-detaille` ou `/stream`).
   * Appelé quand une écoute de `redactionStreamHub` se termine (#857).
   * Effet : si un progress item a un
   * `pendingVerdict` bufferisé (cas où le backend a déjà émis
   * `redaction_plan_results { verdict: 'completed' }`), on flippe MAINTENANT
   * vers `redaction_plan_results` et la carte chat se finalise.
   *
   * Si pas de pending verdict (SSE finit AVANT le verdict backend, cas
   * possible si le streaming est très rapide), on ne fait rien : le futur
   * `redaction_plan_results` flippera immédiatement (le progress item n'aura
   * pas de pendingVerdict, ce qui n'est plus un cas « completed bufferisé »
   * — on tombe en cas B donc flip direct). Voir applyRedactionPlanResults.
   *
   * Idempotent : si l'item est déjà en results (race condition), no-op.
   */
  applyRedactionPlanSseComplete(timeline: TimelineItem[], jobId: string): TimelineItem[] {
    const idx = timeline.findIndex(
      (item) => item.type === 'redaction_plan_progress' && item.jobId === jobId,
    );
    if (idx < 0) return timeline;
    const item = timeline[idx];
    if (!item || item.type !== 'redaction_plan_progress') return timeline;
    if (!item.pendingVerdict) {
      // SSE a fini AVANT que le verdict backend n'arrive — on marque la phase
      // 'complete' pour signaler visuellement la fin du stream. Le futur
      // verdict prendra le relais.
      return timeline.map((it, i) =>
        i === idx && it.type === 'redaction_plan_progress'
          ? { ...it, phase: 'complete' as const }
          : it,
      );
    }
    // Flip différé : on a le verdict bufferisé, le SSE est fini → on finalise.
    return timeline.map((it, i) => {
      if (i !== idx || it.type !== 'redaction_plan_progress') return it;
      const progressSnapshot = captureProgressSnapshot(it);
      return {
        type: 'redaction_plan_results' as const,
        id: it.id,
        requestId: it.requestId,
        jobId: it.jobId,
        kind: it.kind,
        verdict: it.pendingVerdict!,
        proposedPayload: it.proposedPayload,
        extras: it.pendingExtras ?? {},
        ...(progressSnapshot ? { progressSnapshot } : {}),
      };
    });
  }

  /** Annulation utilisateur : retire l'item form (carte annulée disparaît). */
  applyRedactionPlanCancelled(timeline: TimelineItem[], requestId: string): TimelineItem[] {
    return timeline.filter(
      (item) => !(item.type === 'redaction_plan_form' && item.requestId === requestId),
    );
  }

  /**
   * Apply an `agent_ui_actions` event — sorties cliquables proposées par l'agent.
   *
   * Fire-and-forget : pas de `requestId`, pas de verdict à recorréler plus tard,
   * donc pas de `applyAgentUiActionsResult`.
   * L'agent a fini son tour ; le clic pilote une navigation locale chez l'hôte.
   *
   * Remplace un éventuel bloc d'actions précédent du même tour plutôt que d'en
   * empiler un second : deux jeux de boutons concurrents sous une même réponse
   * ne veulent rien dire pour l'avocat, et un agent peut ré-émettre.
   */
  applyAgentUiActions(
    timeline: TimelineItem[],
    payload: AgentUiActionsPayload,
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;

    const result = this.closeActiveContentSegment(this.finalizeThinking(timeline));
    const withoutPrevious = result.filter((item) => item.type !== 'ui_actions');

    // A defensive empty frame means “no proposed continuation”. Replacing the
    // current card prevents stale buttons from surviving a backend correction.
    if (payload.actions.length === 0) return withoutPrevious;

    return [...withoutPrevious, {
      type: 'ui_actions' as const,
      id: this.nextId(),
      actions: payload.actions,
    }];
  }

  /** Add or replay one exact shared skill consent request frame. */
  applySkillChangeRequest(
    timeline: TimelineItem[],
    request: SkillChangeRequest,
  ): TimelineItem[] {
    if (this.isCancelled) return timeline;
    const result = this.closeActiveContentSegment(this.finalizeThinking(timeline));
    const existingIndex = result.findIndex(
      (item) => item.type === 'skill_change' && item.request.requestId === request.requestId,
    );
    if (existingIndex >= 0) {
      // Reconnect replay refreshes the immutable proposal but must never erase an
      // authoritative terminal result already displayed for this correlation id.
      return result.map((item, index) =>
        index === existingIndex && item.type === 'skill_change'
          ? { ...item, request }
          : item,
      );
    }
    return [...result, {
      type: 'skill_change',
      id: this.nextId(),
      request,
    }];
  }

  /** Correlate the backend terminal verdict without inventing client success. */
  applySkillChangeResult(
    timeline: TimelineItem[],
    result: SkillChangeResult,
  ): TimelineItem[] {
    const index = timeline.findIndex(
      (item) => item.type === 'skill_change' && item.request.requestId === result.requestId,
    );
    if (index < 0) return timeline;
    return timeline.map((item, itemIndex) =>
      itemIndex === index && item.type === 'skill_change'
        ? { ...item, result }
        : item,
    );
  }

  /**
   * Mark executed active tools as error and add a disconnected item.
   *
   * `preparing` Canvas cards have no executed action and their transport is
   * intentionally non-replayable. Drop them rather than implying a failed edit
   * after a connection loss.
   * Called when the WebSocket connection drops mid-stream.
   */
  applyDisconnected(timeline: TimelineItem[]): TimelineItem[] {
    const updated = timeline
      .filter(item => item.type !== 'tool' || item.event.status !== 'preparing')
      .map(item => {
      if (item.type === 'tool' && item.event.status !== 'completed' && item.event.status !== 'error') {
        return {
          ...item,
          event: { ...item.event, status: 'error' as const, transportInterrupted: true as const },
        };
      }
      return item;
      });

    if (!updated.some(item => item.type === 'disconnected')) {
      return [...updated, { type: 'disconnected' as const, id: this.nextId() }];
    }
    return updated;
  }

  /**
   * Remove all disconnected items from the timeline.
   * Called on successful reconnection.
   */
  removeDisconnected(timeline: TimelineItem[]): TimelineItem[] {
    return timeline.filter(item => item.type !== 'disconnected');
  }

  /**
   * Ajoute une note de statut discrète (`agent_status_note`, ex. quota fournisseur atteint).
   * Elle ne ferme ni le texte ni le raisonnement en cours : la suite de la réponse continue
   * de s'écrire au-dessus d'elle.
   */
  applyStatusNote(timeline: TimelineItem[], message: string): TimelineItem[] {
    return [...timeline, { type: 'rate_limit_note' as const, id: `note-${this.nextTimestampId()}`, message }];
  }

  /**
   * Reconstruit la timeline du tour à partir de son rattrapage (#1888) : le journal
   * d'affichage du backend, du DÉBUT du tour jusqu'à l'adoption par cette page.
   *
   * WHY repartir de vide : le rattrapage est le tour entier. Ce que la page affichait en
   * l'attendant (note « Hector continue à travailler », marqueur de coupure) n'en fait pas
   * partie, et tout texte déjà reçu y figure aussi.
   * WHY les méthodes du direct : segments de texte, blocs de raisonnement et corrélation des
   * outils sortent identiques à ce que montrait la page d'avant le rechargement.
   * WHY les compteurs d'ids ne repartent pas de zéro : un item rattrapé doit être MONTÉ,
   * jamais mis à jour. `useProgressiveText` affiche d'un bloc le texte présent au montage et
   * anime ce qui s'ajoute à un item déjà monté ; un id réutilisé rejouerait donc l'écriture
   * de tout le rattrapage (≈ 480 caractères par seconde).
   *
   * Seule la timeline est produite : aucun effet de bord des trames du direct (invalidation
   * des pièces, des actions rapides) n'est rejoué, ces effets ont eu lieu avant le rechargement.
   */
  rebuildFromCatchup(timeline: TimelineItem[], entries: readonly TurnDisplayEntry[]): TimelineItem[] {
    if (this.isCancelled) return timeline;

    this.resetStreamAccumulators();
    let result: TimelineItem[] = [];
    for (const entry of entries) {
      switch (entry.type) {
        case 'agent_thinking':
          result = this.applyThinkingDelta(result, entry.payload);
          break;
        case 'agent_message_delta':
          result = this.applyContentDelta(result, entry.payload);
          break;
        case 'agent_message':
          result = this.applyContentSnapshot(result, entry.payload.content);
          break;
        case 'agent_status_note':
          result = this.applyStatusNote(result, entry.payload.message);
          break;
        case 'backend_tool':
          // CAST: le contrat partagé laisse `tool` et `data` ouverts ; c'est la trame
          // `backend_tool` du direct, que le web applique sous ce type sans autre validation.
          result = this.applyToolEvent(result, entry.payload as ToolPayload);
          break;
        default: {
          // Liste fermée (`TurnDisplayEntry`) : une trame ajoutée au journal sans son
          // application ici doit casser la compilation.
          const unhandled: never = entry;
          void unhandled;
        }
      }
    }
    return result;
  }

  /**
   * Add the "finishing on the server" note (2026-09-03): after a reconnect, the backend said
   * the turn is still running where this socket cannot reach it (other instance, or replay
   * window over). Its result comes back through a history reload on `turn_settled`.
   *
   * `cause` only ever gets MORE precise: a note posted before the cause was known is
   * upgraded in place (same id, no remount), and a later call without a cause never erases
   * one — "you were disconnected" is the claim that must not be shown wrongly.
   */
  applyServerFinishing(timeline: TimelineItem[], cause?: ServerFinishingCause): TimelineItem[] {
    if (!timeline.some(item => item.type === 'server_finishing')) {
      return [...timeline, { type: 'server_finishing' as const, id: this.nextId(), ...(cause ? { cause } : {}) }];
    }
    if (!cause) return timeline;
    return timeline.map(item => (item.type === 'server_finishing' && item.cause !== cause ? { ...item, cause } : item));
  }

  /** Remove the "finishing on the server" note once the turn settled (or was superseded). */
  removeServerFinishing(timeline: TimelineItem[]): TimelineItem[] {
    return timeline.filter(item => item.type !== 'server_finishing');
  }

  /**
   * Finalize the session after `agent_done`. Removes orphaned progress and
   * unexecuted Canvas preparation items.
   */
  finalize(timeline: TimelineItem[], reason: DoneReason): TimelineItem[] {
    let result = timeline;

    // For errors, add an error item to the timeline
    if (reason === 'error') {
      // Error item is added by the hook layer (it needs `payload.details`)
      // We only handle orphan cleanup here.
    }

    // Safety net: clean up orphaned progress (failed searches) and ephemeral
    // Canvas activity that never reached a real `started` action.
    result = result.filter(item =>
      item.type !== 'jurisprudence_progress' &&
      !(item.type === 'tool' && item.event.status === 'preparing'),
    );
    this.jurisprudenceQuestions.clear();

    return result;
  }

  /**
   * Cancel the stream: remove unexecuted Canvas preparation, mark active tools
   * as error, and block further deltas.
   */
  cancel(timeline: TimelineItem[]): TimelineItem[] {
    this.isCancelled = true;

    // UI actions belong to the interrupted turn and must never survive as
    // actionable continuations after the user has stopped that turn.
    return timeline
      .filter(item =>
        item.type !== 'ui_actions' &&
        !(item.type === 'tool' && item.event.status === 'preparing'),
      )
      .map(item => {
      if (item.type !== 'tool') return item;
      if (item.event.status === 'completed' || item.event.status === 'error') return item;
      return { ...item, event: { ...item.event, status: 'error' as const } };
      });
  }

  /**
   * Reset all transient state for a new exchange.
   * Called at the start of `sendMessage`.
   */
  reset(): void {
    this.resetStreamAccumulators();
    this.thinkingCounter = 0;
    this.isCancelled = false;
    this.idCounter = 0;
    this.jurisprudenceQuestions.clear();
  }

  /**
   * Clear the cancellation flag. Called at the start of `sendMessage`
   * (before `reset`) to allow deltas from the new exchange.
   */
  uncancel(): void {
    this.isCancelled = false;
  }

  /** Return the accumulated streaming content across all segments. */
  getStreamingContent(): string {
    return this.streamingContent;
  }

  /**
   * Store original questions from `jurisprudence_search_request`
   * so that progress events can display question text.
   */
  setJurisprudenceQuestions(jobId: string, questions: LegalQuestion[]): void {
    this.jurisprudenceQuestions.set(jobId, questions);
  }

  /** Remove stored questions for a completed/cancelled job. */
  removeJurisprudenceQuestions(jobId: string): void {
    this.jurisprudenceQuestions.delete(jobId);
  }
}
